import classNames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { AuthSessionContext } from '@app/context/AuthContext'
import { useSignup } from '@app/context/SignupContext'

import { useCustomerSupport } from '@context/CustomerSupportContext'
import { usePublicDomainsReadContext } from '@context/PublicDomainsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { RichIcon } from '@ui/components/RichIcon'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ChatCircleDotsIcon } from '@ui/icons/ChatCircleDots.icon'
import { CloseIcon } from '@ui/icons/Close.icon'

import { getContactUsLink } from '@utils/companyName.utils'
import { useWindowResize } from '@utils/domUtils'

import { CustomerSupportContact } from '@model/CustomerSupportContact'

import { useHubSpotChatWidget } from '@hooks/chats/useHubSpotChatWidget'
import { useLiveChatWidget } from '@hooks/chats/useLiveChatWidget'

import { Box } from '@shared/components/Box'

import styles from './ChatContactFab.module.scss'

export const ChatContactFab: React.FC = () => {
  const { openHSChat, closeHSChat, isHubSpotVisible } = useHubSpotChatWidget()
  const { liveChatWidget, openLiveChat, closeLiveChat, isLiveChatVisible } = useLiveChatWidget()
  const [isOpen, setIsOpen] = useState(isHubSpotVisible || isLiveChatVisible)
  const [shouldShowOnMobile, setShouldShowOnMobile] = useState(false)
  const { domains } = usePublicDomainsReadContext()
  const [auth] = useContext(AuthSessionContext)
  const isAuthenticated = !!auth
  const { signupData } = useSignup()
  const [isVisible, setIsVisible] = useState(true)
  const isMobile = useWindowResize()

  const showAutomatically = !isAuthenticated
    ? signupData.values.tickmillCompanyId &&
      signupData.values.productIds &&
      signupData.values.productIds.length > 0
    : !isMobile || shouldShowOnMobile

  const locale = useSessionLanguage()
  const { enabledContacts = [], isLoading } = useCustomerSupport()
  const location = useLocation()
  const initialRender = useRef(true)
  const tooltipDirection = 'left'

  const { t } = useTranslation()

  useEffect(() => {
    const handleShowChatFab = () => {
      if (isMobile && isAuthenticated) {
        setShouldShowOnMobile(true)
        setIsVisible(true)

        if (enabledContacts.length === 1 && isHubSpotContact(enabledContacts[0])) {
          if (isLiveChatVisible) {
            closeLiveChat()
          }
          openHSChat()
        } else if (enabledContacts.length > 0) {
          setIsOpen(true)
        }
      }
    }

    document.addEventListener('show-chat-fab', handleShowChatFab)

    return () => {
      document.removeEventListener('show-chat-fab', handleShowChatFab)
    }
  }, [isMobile, isAuthenticated, enabledContacts, openHSChat, closeLiveChat, isLiveChatVisible])

  useEffect(() => {
    if (!initialRender.current) {
      setIsOpen(false)
      closeHSChat()
      closeLiveChat()

      if (isMobile && isAuthenticated) {
        setIsVisible(false)
        setShouldShowOnMobile(false)
      }
    }
    initialRender.current = false
  }, [location.pathname, closeHSChat, closeLiveChat, isMobile, isAuthenticated])

  const isHubSpotContact = (contact: CustomerSupportContact) =>
    contact.id === 'hubspot' || contact.nameId === 'hubspot' || contact.name === 'hubspot'

  const isLiveChatContact = (contact: CustomerSupportContact) =>
    contact.id === 'livechat' || contact.nameId === 'livechat' || contact.name === 'livechat'

  const handleContactClick = useCallback(
    (contact: CustomerSupportContact) => {
      if (isHubSpotContact(contact)) {
        if (isLiveChatVisible) {
          closeLiveChat()
        }

        openHSChat()
      } else if (isLiveChatContact(contact)) {
        if (isHubSpotVisible) {
          closeHSChat()
        }
        openLiveChat()
      } else {
        const urls = contact.urls || []
        const urlData =
          urls.find((u) => u.languageId === locale) ||
          urls.find((u) => u.default) ||
          (urls.length > 0 ? urls[0] : null)

        if (urlData?.url) {
          const rawUrl = urlData.url.trim()
          let targetUrl: string

          try {
            targetUrl = new URL(rawUrl).href
          } catch {
            targetUrl = new URL(`https://${rawUrl}`).href
          }

          window.open(targetUrl, '_blank')
        }
      }
    },
    [
      locale,
      openHSChat,
      openLiveChat,
      closeHSChat,
      closeLiveChat,
      isHubSpotVisible,
      isLiveChatVisible,
    ]
  )

  const handleFabClick = useCallback(() => {
    if (enabledContacts.length === 0) {
      window.open(
        getContactUsLink(domains, locale),
        '_blank',
        'location=yes,height=570,width=520,scrollbars=yes,status=yes'
      )
      return
    }

    if (isOpen) {
      closeHSChat()
      closeLiveChat()
      setIsOpen(false)

      if (isMobile && isAuthenticated) {
        setTimeout(() => {
          setIsVisible(false)
        }, 5000)
      }
    } else {
      setIsOpen(true)
    }
  }, [closeHSChat, closeLiveChat, domains, enabledContacts.length, isOpen, locale, isMobile])

  if (isLoading || !showAutomatically || !isVisible) {
    return null
  }

  return (
    <div className={classNames(styles.chatFabContainer)}>
      {liveChatWidget}
      <AnimatePresence>
        {isOpen && enabledContacts.length > 0 && (
          <motion.div
            className={styles.optionsContainer}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            {enabledContacts.map((contact: CustomerSupportContact, index) => (
              <Tooltip
                disabled={!contact.logo?.tooltipText || isMobile}
                content={contact.logo.tooltipText || contact.name}
                key={contact.id || contact.nameId || `contact-${index}`}
                direction={tooltipDirection}
              >
                <Box
                  className={classNames(styles.optionItem, styles.noMinWidth)}
                  onClick={() => handleContactClick(contact)}
                >
                  <div className={styles.optionContent}>
                    <div className={styles.optionIcon}>
                      {contact.logo?.url ? (
                        <img src={contact.logo.url} alt={contact.name} />
                      ) : (
                        <span>{contact.name}</span>
                      )}
                    </div>
                  </div>
                </Box>
              </Tooltip>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <Tooltip disabled={isMobile} content={t('Chat with us')} direction={tooltipDirection}>
        <motion.div
          whileHover={{ scale: 1.05 }}
          className={styles.fabButton}
          onClick={handleFabClick}
        >
          {isOpen ? (
            <RichIcon
              shape='circle'
              size='x56'
              icon={{ icon: CloseIcon, color: 'text-white' }}
              bg='surface-info-high-em'
            />
          ) : (
            <RichIcon
              shape='circle'
              size='x56'
              icon={{ icon: ChatCircleDotsIcon, color: 'text-white' }}
              bg='surface-info-high-em'
            />
          )}
        </motion.div>
      </Tooltip>
    </div>
  )
}
